import { apiCall } from 'app/shared/lib/essette-api';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { autoinject, bindable } from 'aurelia-framework';
import { MemberService } from 'app/cd/services/member-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { IPopup } from 'app/shared/elements/popup-base';
import { deepClone } from 'app/shared/lib/utilities';
import { LookupService } from 'app/core/services/lookup-service';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class MemberContacts extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable()
    protected memberId: string | null = null;

    protected memberContacts: CD.MemberContact[] = [];
    protected sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected validateForm = validate;
    protected editedContact: CD.MemberContact | null = null;

    protected relationships: ReadonlyArray<CD.MemberRelationship> = [];
    protected types: ReadonlyArray<CD.MemberContactType> = [];
    protected regions: ReadonlyArray<Core.AddressRegion> = [];

    protected systemCodes: {
        [key: string]: string
    } = {};

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    private $saveContactModal!: IPopup;
    protected $saveContactForm!: IForm;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;

        this.checkPermissions();
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        if(!this.memberId) return;
        await this.loadMemberContacts();
        await this.loadLookups();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }
    protected closeSaveContactModal(): void {
        if(this.$saveContactForm.reset) {
            this.$saveContactForm.reset();
        }
        this.$saveContactModal.close();
    }

    protected async handleSaveContactFormSuccess(): Promise<void> {
        if(this.$saveContactForm.reset) {
            this.$saveContactForm.reset();
        }
        this.$saveContactModal.close();
        await this.reload();
    }

    protected saveContact(contact: CD.MemberContact | null = null): void {
        this.editedContact = contact ? deepClone(contact) : {};
        this.$saveContactModal.open();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadMemberContacts(): Promise<void> {
        if(!this.memberId) return;

        // use default sort if no other sort specified
        let sort = new Sorting();
        if(this.sorting.entries.length === 0) {
            sort.add({ reference: 'name.full', direction: SortDirection.Ascending })
        }
        else sort = this.sorting;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: sort,
            includeInactive: this.includeInactive
        };

        const includes: string[] = [
            // member fields
            'relationship',
            'types',
            // non-member fields
            'address.mailing',
            'phoneNumber.home',
            'phoneNumber.cell',
            'phoneNumber.work',
            'phoneNumber.fax',
            // related member fields
            'relatedMember',
            'relatedMember.phoneNumber',
            'relatedMember.address.mailing',
            'relatedMember.phoneNumber.home',
            'relatedMember.phoneNumber.cell',
            'relatedMember.phoneNumber.work',
            'relatedMember.phoneNumber.fax',
        ];

        const fields: string[] = [
            // member fields
            'name.full',
            'optout',
            'isActive',
            'relationship.description',
            'types.description',
            // non-member fields
            'emailAddress',
            'identifier.external',
            'address.mailing.*',
            'phoneNumber.*',
            // related member fields
            'relatedMember.emailAddress',
            'relatedMember.description',
            'relatedMember.identifier.external',
            'relatedMember.address.mailing.*',
            'relatedMember.phoneNumber.*',

        ];

        try {
            this.state.beginLoad();
            const response = await this.memberService.loadMemberContacts(this.memberId, options, includes, fields);
            this.memberContacts = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const [relationships, types, regions, addressTypes, phoneNumberTypes] = await Promise.all(
                [
                    this.memberService.loadMemberRelationships(),
                    this.memberService.loadMemberContactTypes(),
                    this.lookupService.loadStates(),
                    this.memberService.loadMemberContactAddressTypes(),
                    this.memberService.loadMemberContactPhoneNumberTypes()
                ]);

            this.relationships = relationships.results;
            this.types = types.results;
            this.regions = regions.results;
            // Aggregate address types and phone number types, pairing them to their IDs
            ([...addressTypes.results, ...phoneNumberTypes.results] as (CD.MemberAddressType | CD.MemberPhoneNumberType)[]).forEach(type => {
                if(type.systemCode && type.id) {
                    this.systemCodes[type.systemCode] = type.id
                }
            });
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateContact(contact: CD.MemberContactComposite): Promise<void> {
        if(!contact.id) return;

        // Show confirmation dialog only if deactivating
        if(contact.isActive) {
            const result = await confirmation.showDeactivate('cd:memberContact.$', 1, false);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        try {
            this.state.beginSave();
            const response = await this.memberService.activateMemberContact(contact.id, !contact.isActive);

            if(response.isSuccessful) {
                this.reload();
                this.toaster.standard.activate('cd:memberContact.$', !contact.isActive, response.isSuccessful);
            }
        }
        catch(error) {
            this.toaster.standard.activate('cd:memberContact.$', !contact.isActive, false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
